import { useState } from 'react';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const tipoEventoLabels: Record<string, string> = {
  'pre-wedding': 'Pré-Wedding',
  'casamento': 'Casamento',
  'sessao-familia': 'Sessão Família',
  'celebracao': 'Celebração',
  'corporativo': 'Evento Corporativo',
  'outros': 'Outros',
};

export default function Contato() {
  const [submitted, setSubmitted] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    tipoEvento: '',
    outrosEstilo: '',
    convidados: '',
    data: '',
    mensagem: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const tipoLabel = tipoEventoLabels[formData.tipoEvento] || formData.tipoEvento;

    let msg = `Olá! Me chamo ${formData.nome} e gostaria de solicitar um orçamento.`;
    msg += `\n\n📧 E-mail: ${formData.email}`;
    if (formData.telefone) msg += `\n📱 Telefone: ${formData.telefone}`;
    if (tipoLabel) msg += `\n📅 Tipo de evento: ${tipoLabel}`;
    if (formData.tipoEvento === 'outros' && formData.outrosEstilo) {
      msg += `\n🎨 Estilo do evento: ${formData.outrosEstilo}`;
    }
    if (formData.convidados) msg += `\n👥 Convidados: ${formData.convidados}`;
    if (formData.data) msg += `\n📆 Data prevista: ${formData.data}`;
    msg += `\n\n💬 Sobre o evento:\n${formData.mensagem}`;

    const url = `https://wa.me/5515988175816?text=${encodeURIComponent(msg)}`;
    setWhatsappUrl(url);
    setSubmitted(true);

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const schema = [
    {
      '@type': 'ContactPage',
      name: 'Contato - Jéssica Bonini Fotógrafa',
      description: 'Entre em contato para solicitar um orçamento de fotografia para casamentos, pré-wedding, sessão família e eventos corporativos.',
      url: `${SITE_URL}/contato`,
      inLanguage: 'pt-BR',
      mainEntity: {
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#business`,
        name: 'Jéssica Bonini Fotógrafa',
        telephone: '+55-15-98817-5816',
        email: 'contato@jessicabonini.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Sorocaba',
          addressRegion: 'SP',
          addressCountry: 'BR',
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '10:00',
            closes: '18:00',
          },
        ],
      },
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contato | Jéssica Bonini Fotógrafa - Orçamento de Fotografia"
        description="Entre em contato com a Jéssica Bonini Fotógrafa para solicitar um orçamento personalizado para casamentos, pré-wedding, sessão família e eventos corporativos em Sorocaba e região."
        keywords="contato fotógrafa, orçamento fotografia, fotografia casamento sorocaba, pré-wedding orçamento"
        canonicalPath="/contato"
        ogType="website"
        schema={schema}
      />
      <Navbar />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
          <div className="px-6 md:px-10 lg:px-16 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-warm-600 mb-4 block">
              Contato
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dark font-normal leading-tight mb-6">
              Vamos <span className="italic">conversar</span>
            </h1>
            <p className="text-dark/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Entre em contato para solicitar um orçamento ou tirar dúvidas. Será um prazer atendê-lo.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20">
              <div className="w-full lg:w-2/5">
                <div className="bg-cream rounded-2xl p-8 md:p-10 mb-8">
                  <h3 className="font-serif text-2xl text-dark mb-6">Informações</h3>
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-warm-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark mb-1">Localização</p>
                        <p className="text-sm text-dark/60">Sorocaba, SP</p>
                        <p className="text-sm text-dark/60">Registrando histórias através do Brasil. Disponível para casamentos e eventos em todo o território nacional.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-warm-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark mb-1">E-mail</p>
                        <p className="text-sm text-dark/60">contato@jessicabonini.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-warm-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark mb-1">Telefone</p>
                        <a
                          href="https://wa.me/5515988175816"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-dark/60 hover:text-warm-600 transition-colors"
                        >
                          (15) 9 8817-5816
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-warm-600 flex-shrink-0">
                        <i className="ri-time-line text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark mb-1">Horário</p>
                        <p className="text-sm text-dark/60">Seg - Sáb: 10h às 18h</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-dark mb-4 tracking-wide uppercase">Redes Sociais</h4>
                  <div className="flex gap-3">
                    <a
                      href="https://www.instagram.com/jessicaboninifotografa"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center rounded-full bg-cream text-dark/60 hover:bg-dark hover:text-white transition-all duration-300"
                      aria-label="Instagram"
                    >
                      <i className="ri-instagram-line text-lg" />
                    </a>
                    <a
                      href="https://wa.me/5515988175816"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center rounded-full bg-cream text-dark/60 hover:bg-dark hover:text-white transition-all duration-300"
                      aria-label="WhatsApp"
                    >
                      <i className="ri-whatsapp-line text-lg" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-3/5">
                {submitted ? (
                  <div className="bg-cream rounded-2xl p-10 md:p-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-6">
                      <i className="ri-whatsapp-line text-2xl" />
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl text-dark mb-4">Mensagem pronta!</h3>
                    <p className="text-dark/60 leading-relaxed mb-6">
                      Seu orçamento foi montado com todos os detalhes. O WhatsApp foi aberto em uma nova aba para você enviar diretamente.
                    </p>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-all duration-300 whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-sm" />
                      Abrir WhatsApp
                    </a>
                  </div>
                ) : (
                  <form
                    id="form-contato"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="nome" className="block text-sm font-medium text-dark mb-2">Nome completo</label>
                        <input
                          type="text"
                          id="nome"
                          name="nome"
                          required
                          value={formData.nome}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                          placeholder="Seu nome"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-dark mb-2">E-mail</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                          placeholder="seu@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label htmlFor="telefone" className="block text-sm font-medium text-dark mb-2">Telefone</label>
                        <input
                          type="tel"
                          id="telefone"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                          placeholder="(00) 00000-0000"
                        />
                      </div>
                      <div>
                        <label htmlFor="tipoEvento" className="block text-sm font-medium text-dark mb-2">Tipo de evento</label>
                        <select
                          id="tipoEvento"
                          name="tipoEvento"
                          value={formData.tipoEvento}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors appearance-none cursor-pointer"
                        >
                          <option value="">Selecione</option>
                          <option value="pre-wedding">Pré-Wedding</option>
                          <option value="casamento">Casamento</option>
                          <option value="sessao-familia">Sessão Família</option>
                          <option value="celebracao">Celebração</option>
                          <option value="corporativo">Evento Corporativo</option>
                          <option value="outros">Outros</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="convidados" className="block text-sm font-medium text-dark mb-2">Quantidade de convidados</label>
                        <input
                          type="number"
                          id="convidados"
                          name="convidados"
                          min={1}
                          value={formData.convidados}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                          placeholder="Ex: 120"
                        />
                      </div>
                    </div>

                    {formData.tipoEvento === 'outros' && (
                      <div>
                        <label htmlFor="outrosEstilo" className="block text-sm font-medium text-dark mb-2">
                          Qual o estilo do evento?
                        </label>
                        <input
                          type="text"
                          id="outrosEstilo"
                          name="outrosEstilo"
                          required
                          value={formData.outrosEstilo}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                          placeholder="Descreva o estilo do seu evento"
                        />
                      </div>
                    )}

                    <div>
                      <label htmlFor="data" className="block text-sm font-medium text-dark mb-2">Data prevista do evento</label>
                      <input
                        type="date"
                        id="data"
                        name="data"
                        value={formData.data}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="mensagem" className="block text-sm font-medium text-dark mb-2">
                        Mensagem <span className="text-warm-600">*</span>
                      </label>
                      <p className="text-xs text-dark/50 mb-2">
                        Descreva os detalhes e instruções sobre o evento para que possamos preparar um orçamento completo e exclusivo para você.
                      </p>
                      <textarea
                        id="mensagem"
                        name="mensagem"
                        rows={5}
                        maxLength={500}
                        required
                        value={formData.mensagem}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-warm-200 bg-cream/50 text-dark text-sm focus:outline-none focus:border-warm-400 transition-colors resize-none"
                        placeholder="Conte sobre o local, horário, estilo desejado, necessidades especiais e tudo o que acha importante para seu evento..."
                      />
                      <p className="text-xs text-dark/40 mt-1 text-right">{formData.mensagem.length}/500</p>
                    </div>

                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-all duration-300 self-start whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-sm" />
                      Enviar no WhatsApp
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}